import { ArrowRight, Bot } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AssistantGlyph } from '@/components/brand/AssistantGlyph'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

export interface SpectreAgentCardViewProps {
  /** Number of existing chat conversations for the case. */
  conversationCount: number
  /** Called with the trimmed prompt on submit (empty string when blank). */
  onStart: (prompt: string) => void
  /** Ready questions under the input; picking one starts a conversation with it. */
  suggestions?: readonly string[]
}

const BACKDROP_STYLE = `
        .spectre-agent-bot {
          filter: blur(11px);
          opacity: 0.26;
          animation: spectre-agent-breathe 9s ease-in-out infinite;
          transform-origin: 70% 30%;
        }
        @keyframes spectre-agent-breathe {
          0%, 100% { transform: scale(1);    opacity: 0.26; }
          50%      { transform: scale(1.05); opacity: 0.34; }
        }
        @media (prefers-reduced-motion: reduce) {
          .spectre-agent-bot {
            animation: none;
          }
        }
      `

function Decoration() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl"
    >
      {/* Hidden, blurred Bot silhouette — the card's signature flourish. */}
      <div className="spectre-agent-bot absolute -top-14 -right-16 flex size-[320px] items-center justify-center text-primary">
        <Bot strokeWidth={1.4} className="size-[300px]" />
      </div>

      {/* Soft copper aura layered over the bot — gives it presence
            without lifting it out of the background. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(440px 320px at 100% -8%, color-mix(in srgb, var(--primary) 22%, transparent), transparent 62%), radial-gradient(220px 200px at 0% 110%, color-mix(in srgb, var(--primary) 6%, transparent), transparent 60%)',
        }}
      />
    </div>
  )
}

function AgentHeading({ conversationCount }: Pick<SpectreAgentCardViewProps, 'conversationCount'>) {
  const { t } = useTranslation()
  const subtitle =
    conversationCount === 0
      ? t('overview.spectreAgent.subtitleEmpty')
      : t('overview.spectreAgent.subtitleConversations', { count: conversationCount })
  return (
    <div className="flex items-start gap-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-primary shadow-[0_0_0_4px_color-mix(in_srgb,var(--primary)_5%,transparent)]">
        <Bot className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold text-foreground">
          {t('overview.spectreAgent.title')}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  )
}

function PromptForm({ onStart }: Pick<SpectreAgentCardViewProps, 'onStart'>) {
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState('')
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onStart(prompt.trim())
  }
  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center gap-3 rounded-xl border border-primary/50 bg-background py-2 pr-2 pl-4 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 md:px-4 md:py-3"
    >
      <AssistantGlyph className="size-4 shrink-0 max-md:hidden" />
      <input
        type="text"
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder={t('overview.spectreAgent.cta')}
        className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground max-md:h-9 max-md:text-ellipsis md:text-sm"
        aria-label={t('overview.spectreAgent.cta')}
      />
      <Button
        type="submit"
        variant="solid"
        size="icon-sm"
        className="size-9 shrink-0 rounded-full hover:translate-x-0.5 md:size-7"
        aria-label={t('overview.spectreAgent.cta')}
      >
        <ArrowRight className="size-3.5" />
      </Button>
    </form>
  )
}

function ReadyQuestions({
  onStart,
  suggestions = [],
}: Pick<SpectreAgentCardViewProps, 'onStart' | 'suggestions'>) {
  if (suggestions.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map(suggestion => (
        <Button
          key={suggestion}
          type="button"
          variant="outline"
          size="xs"
          className="rounded-full bg-background/60"
          onClick={() => onStart(suggestion)}
        >
          {suggestion}
        </Button>
      ))}
    </div>
  )
}

/** Pure assistant entry point; the connected card supplies the count and navigation. */
export function SpectreAgentCardView({
  conversationCount,
  onStart,
  suggestions,
}: SpectreAgentCardViewProps) {
  return (
    <Card className="relative isolate p-4 md:p-6">
      <Decoration />
      <div className="relative z-10 flex flex-col gap-4">
        <AgentHeading conversationCount={conversationCount} />
        <PromptForm onStart={onStart} />
        <ReadyQuestions onStart={onStart} suggestions={suggestions} />
      </div>
      <style>{BACKDROP_STYLE}</style>
    </Card>
  )
}
